import React, { useState, useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import CopyableText from "../generic/CopyableText";
import CustomTooltip from "../generic/CustomTooltip";
import "@fortawesome/fontawesome-free/css/all.min.css";

const PodCard = ({ setShowPodInfo, podInfo, dataLoaded, isSelected }) => {
  const [isLoaded, setIsLoaded] = useState(false); // State to control component loading

  useEffect(() => {
    if (dataLoaded) {
      setIsLoaded(true);
    }
  }, [dataLoaded]);

  if (!isLoaded)
    return (
      <Card>
        <Card.Body>Loading...</Card.Body>
      </Card>
    );
  if (!podInfo)
    return (
      <Card>
        <Card.Body>No pod data available</Card.Body>
      </Card>
    );

  // Handle pod status
  const statusColours = {
    Running: "#1B9E3E", // pod running (green)
    true: "#1B9E3E", // container ready = true (green)
    Pending: "#FFC107", // pod pending (yellow)
    false: "#FFC107", // container ready = false (yellow)
    ContainerCreating: "#FFC107", // containerCreating (yellow)
    Terminating: "#FFC107", // pod terminating (yellow)
    Stopped: "#DC3545", // pod stopped (red)
    Failed: "#DC3545", // pod failed (red)
    Unknown: "#6C757D", // pod status unknown (grey)
  };

  // functions to handle pod data
  const getStatusColour = (status) => {
    return statusColours[status] || statusColours["Unknown"];
  };

  const formatDuration = (status, podUptime) => {
    // Check if status is not Running
    if (status !== "Running") {
      return `Pod is ${status.toLowerCase()}`;
    }

    // Check if uptime exists & is a string
    if (!podUptime || typeof podUptime !== "string") {
      console.error("Invalid uptime format:", podUptime);
      return "Uptime: Unknown";
    }

    // If not an ISO duration (doesn't start with PT), return as-is
    if (!podUptime.startsWith("PT")) {
      return `Uptime: ${podUptime}`;
    }

    try {
      // Process ISO duration format
      const timeStr = podUptime.replace("PT", "");

      // Extract hours, minutes, seconds
      const hours = parseInt(timeStr.match(/(\d+)H/)?.[1] || 0);
      const minutes = parseInt(timeStr.match(/(\d+)M/)?.[1] || 0);
      const seconds = parseFloat(timeStr.match(/(\d+\.?\d*)S/)?.[1] || 0);

      // Convert to days if more than 24 hours
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;

      const timeFormats = [
        { condition: days > 0, format: () => `${days}d ${remainingHours}h` },
        { condition: hours > 0, format: () => `${hours}h ${minutes}m` },
        {
          condition: minutes > 0,
          format: () => `${minutes}m ${Math.round(seconds)}s`,
        },
      ];

      const format = timeFormats.find(({ condition }) => condition);
      return format ? format.format() : `Uptime: ${Math.round(seconds)}s`;
    } catch (error) {
      // Log error and return 'Unknown' if parsing fails
      console.error("Error parsing duration:", error);
      return "Uptime: Unknown";
    }
  };

  const formatContainerName = (name) => {
    if (!name) return "";
    return name
      .split("-")
      .map((part) => (/\d/.test(part) ? part : part.charAt(0).toUpperCase()))
      .join("");
  };

  const formatCpu = (cpuValue) => {
    if (!cpuValue) return "0";
    // Remove 'n' and convert to number
    const numericValue = parseInt(cpuValue.replace("n", ""));
    // Convert nanocores to millicores (more commonly used in K8s)
    const millicores = (numericValue / 1000000).toFixed(2);
    return `${millicores}m`;
  };

  const formatMemory = (memoryValue) => {
    if (!memoryValue) return "0";
    const kibibytes = parseInt(memoryValue.replace("Ki", ""));

    if (kibibytes >= 1048576) {
      // 1 GiB = 1048576 KiB
      return `${(kibibytes / 1048576).toFixed(2)} GiB`;
    } else if (kibibytes >= 1024) {
      // 1 MiB = 1024 KiB
      return `${(kibibytes / 1024).toFixed(2)} MiB`;
    } else {
      return `${kibibytes}Kib`;
    }
  };

  // Parse values as required
  const appName =
    podInfo.pod_name.split("-").slice(0, -2).join("-") || "Unknown"; // Default pod name
  const podId = podInfo.pod_name.split("-").slice(-2).join("-") || "Unknown"; // Default pod ID
  const podStatus = podInfo.status || "Unknown"; // Default pod status
  const podUptime = formatDuration(podStatus, podInfo.uptime) || "Unknown"; // Default pod uptime
  const containerNames =
    podInfo.containers.map((container) => container.name).join(", ") ||
    "Unknown"; // Default container names

  // handle horizontal scrolling
  const handleWheel = (e) => {
    e.preventDefault();
    e.currentTarget.scrollLeft += e.deltaY;
  };

  return (
    <Card
      style={{
        width: "auto",
        height: "100%",
        border: isSelected ? "2px solid #444" : "1px solid #ddd",
        borderRadius: "8px",
        position: "relative",
      }}
      onClick={() => {
        setShowPodInfo(true);
      }}
    >
      <Card.Body className="d-flex justify-content-between align-items-start pb-0">
        {/* Pod Icon */}
        <div
          className="podIcon d-flex align-items-center mb-2"
          style={{
            backgroundColor: getStatusColour(podStatus),
            padding: "8px",
            borderRadius: "4px",
          }}
        >
          <i
            className="fa-solid fa-boxes-stacked"
            style={{ color: "#fff" }}
          ></i>
        </div>
        {/* App Name and POD Identifiers */}
        <div className="podDetails" style={{ padding: "0 8px", width: "100%" }}>
          <Row className="w-100">
            <Col>
              <div
                className="podDetails"
                style={{ padding: "0 8px", width: "100%" }}
              >
                <CopyableText
                  text={appName}
                  textToCopy={podInfo.pod_name}
                  label="App Name"
                  tooltipText="Click to copy"
                  style={{
                    fontSize: "1rem",
                    letterSpacing: "0.5px",
                    fontWeight: "bold",
                    marginBottom: "0",
                  }}
                />
                <p
                  className="text-muted mb-1"
                  style={{
                    fontSize: "0.8rem",
                    letterSpacing: "0.25px",
                  }}
                >
                  {podId}
                </p>
              </div>
            </Col>
          </Row>
        </div>

        {/* Hardware Info */}
        <div
          className="podHardware"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            whiteSpace: "nowrap",
          }}
        >
          {/* CPU */}
          <CustomTooltip content={<p>CPU Allocation</p>}>
            <div className="d-flex align-items-center mb-2">
              <i
                className="fa-solid fa-server"
                style={{
                  marginRight: "8px",
                  width: "16px",
                  textAlign: "center",
                  color: "blue",
                }}
              ></i>
              <span
                style={{
                  fontSize: "0.9rem",
                  letterSpacing: "0.25px",
                }}
              >
                {podInfo.usage.cpu ? formatCpu(podInfo.usage.cpu) : "N/A"}
              </span>
            </div>
          </CustomTooltip>
          {/* Memory */}
          <CustomTooltip content={<p>RAM Allocation</p>}>
            <div className="d-flex align-items-center mb-2">
              <i
                className="fa-solid fa-microchip"
                style={{
                  marginRight: "8px",
                  width: "16px",
                  textAlign: "center",
                  color: "#FFC107",
                }}
              ></i>
              <span
                style={{
                  fontSize: "0.9rem",
                  letterSpacing: "0.25px",
                }}
              >
                {podInfo.usage.memory
                  ? formatMemory(podInfo.usage.memory)
                  : "N/A"}
              </span>
            </div>
          </CustomTooltip>
        </div>
      </Card.Body>
      <Card.Body
        className="d-flex justify-content-between align-items-start pt-0"
        style={{
          position: "relative",
          marginTop: "10px",
        }}
      >
        {/* Pod IP */}
        <div className="podIngress w-100">
          <CopyableText
            text={podInfo.ingress_urls[0]}
            label="Pod URL"
            tooltipText="Click to copy"
            style={{
              color: "#007bff",
              wordBreak: "break-all",
              fontSize: "1rem",
            }}
          />
        </div>
      </Card.Body>
      {/* List of Containers & Pod Status */}
      <Card.Body
        className="container-body d-flex justify-content-around pt-3 pb-2"
        style={{
          position: "relative",
          marginTop: "10px",
        }}
      >
        {/* Container List */}
        <div
          className="container-div"
          onWheel={handleWheel}
          style={{
            position: "absolute",
            bottom: "10px",
            left: "10px",
            display: "flex",
            flexDirection: "row",
            flexWrap: "nowrap",
            gap: "8px",
            zIndex: 1,
            maxWidth: "calc(100% - 100px)",
            overflowX: "auto",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            paddingBottom: "2px",
            cursor: "all-scroll",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "8px",
              paddingRight: "16px",
            }}
          >
            {podInfo.containers &&
              podInfo.containers.map((container, index) => (
                <CustomTooltip content={<p>{container.name}</p>}>
                  <div
                    className="container"
                    style={{
                      backgroundColor: getStatusColour(
                        container.ready === undefined ||
                          container.nodeName === "N/A"
                          ? "false"
                          : container.ready.toString()
                      ),
                      color: "#fff",
                      padding: "4px 8px",
                      borderRadius: "16px",
                      fontSize: "0.8rem",
                      fontWeight: "500",
                      flexShrink: 0,
                      width: "fit-content",
                      textAlign: "center",
                      transition: "opacity 0.2s",
                      cursor: "pointer",
                    }}
                  >
                    {formatContainerName(containerNames.split(", ")[index])}
                  </div>
                </CustomTooltip>
              ))}
          </div>
        </div>
        {/* Pod Status */}
        <CustomTooltip content={<p>{podUptime}</p>}>
          <div
            className="status-div"
            style={{
              position: "absolute",
              bottom: "10px",
              right: "10px",
              border: `2px solid ${getStatusColour(podStatus)}`,
              backgroundColor: "white",
              color: getStatusColour(podStatus),
              padding: "4px 12px",
              borderRadius: "16px",
              fontSize: "0.8rem",
              fontWeight: "500",
              zIndex: 1,
            }}
          >
            {podStatus}
          </div>
        </CustomTooltip>
      </Card.Body>
    </Card>
  );
};

export default PodCard;
